import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { registerSchema, ROLE_OPTIONS, type RegisterFormValues } from '../lib/validations';
import { registerUser, type AutoAcceptResult } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import { useOrgStore } from '../store/orgStore';
import api from '../lib/axios';
import Wordmark from '../components/Brand';
import type { ApiResponse, LoginResponse } from '@maios/shared';
import { UserPlus, AlertCircle, Mail } from 'lucide-react';
import { INVITATION_STATUS_LABELS, AUTO_ACCEPT_REASON_MESSAGES, type InvitationStatus } from '../constants/invitationStatusLabels';

// CHG-141 REQ-IF-012: 회원가입 진입 시 초대 토큰 정보 표시·검증.
interface InvitationContext {
  email: string;
  organizationName: string;
  status: InvitationStatus;
  type: 'ORG' | 'PROJECT';
}

interface OrgInvitationGetResponse {
  email: string;
  organizationName: string;
  status: InvitationStatus;
}

interface ProjectInvitationGetResponse {
  inviteeEmail: string;
  status: InvitationStatus;
  project: { name: string };
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // CHG-141: 신규 형식 ?invitation=...&type=ORG|PROJECT (구 형식 ?invitationToken=...도 호환 — ORG 가정)
  const invitationToken = searchParams.get('invitation') ?? searchParams.get('invitationToken');
  const invitationType = (searchParams.get('type') as 'ORG' | 'PROJECT' | null) ?? 'ORG';
  const setAuth = useAuthStore((s) => s.setAuth);
  const { setActiveOrgId, fetchOrganizations } = useOrgStore();

  const [invitationCtx, setInvitationCtx] = useState<InvitationContext | null>(null);
  const [invitationLoading, setInvitationLoading] = useState(!!invitationToken);
  const [invitationError, setInvitationError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { role: 'DEVELOPER' },
  });

  // 초대 토큰이 있으면 정보 조회 + email readonly 채움
  useEffect(() => {
    if (!invitationToken) return;
    (async () => {
      setInvitationLoading(true);
      try {
        if (invitationType === 'ORG') {
          const res = await api.get<ApiResponse<OrgInvitationGetResponse>>(`/org-invitations/${invitationToken}`);
          const data = res.data.data!;
          setInvitationCtx({ email: data.email, organizationName: data.organizationName, status: data.status, type: 'ORG' });
          setValue('email', data.email);
        } else {
          const res = await api.get<ApiResponse<ProjectInvitationGetResponse>>(`/invitations/${invitationToken}`);
          const data = res.data.data!;
          setInvitationCtx({ email: data.inviteeEmail, organizationName: data.project.name, status: data.status, type: 'PROJECT' });
          setValue('email', data.inviteeEmail);
        }
      } catch {
        setInvitationError('초대 정보를 불러올 수 없습니다.');
      } finally {
        setInvitationLoading(false);
      }
    })();
  }, [invitationToken, invitationType, setValue]);

  const onSubmit = async (data: RegisterFormValues) => {
    try {
      const result = await registerUser({
        email: data.email,
        password: data.password,
        name: data.name,
        role: data.role,
        // CHG-141 REQ-IF-006: 본문에 초대 토큰 동봉
        pendingInvitationToken: invitationToken ?? undefined,
        pendingInvitationType: invitationToken ? invitationType : undefined,
      });

      // SKIP_EMAIL_VERIFICATION 모드 — 즉시 로그인 + 서버가 직접 자동 합류 수행 (CHG-141 BUG-004 fix)
      if (result.data && 'tokens' in result.data) {
        const loginData = result.data as LoginResponse & { autoAccept?: AutoAcceptResult | null };
        setAuth(loginData.user, loginData.tokens.accessToken, loginData.tokens.refreshToken);

        // VerifyEmailPage와 동일 패턴 — autoAccept 응답 처리.
        // navigate에 replace:true 적용 — GuestRoute가 인증 감지 후 / 로 보내는 race 회피.
        const autoAccept = loginData.autoAccept;
        if (autoAccept?.status === 'accepted') {
          if (autoAccept.organizationId) {
            await fetchOrganizations();
            setActiveOrgId(autoAccept.organizationId);
            toast.success(`조직 ${autoAccept.organizationName ?? ''}에 합류했습니다!`);
            navigate('/', { replace: true });
            return;
          }
          if (autoAccept.projectId) {
            toast.success('프로젝트에 합류했습니다!');
            navigate(`/projects/${autoAccept.projectKey ?? autoAccept.projectId}`, { replace: true });
            return;
          }
        }
        if (autoAccept?.status === 'skipped' && autoAccept.reason) {
          const msg = AUTO_ACCEPT_REASON_MESSAGES[autoAccept.reason] ?? '자동 합류에 실패했습니다.';
          toast(msg, { icon: 'ℹ️' });
          // already_member면 조직 활성화 후 홈으로
          if (autoAccept.reason === 'already_member' && autoAccept.organizationId) {
            await fetchOrganizations();
            setActiveOrgId(autoAccept.organizationId);
            navigate('/', { replace: true });
          } else {
            navigate('/onboarding', { replace: true });
          }
          return;
        }

        // 토큰 없는 일반 가입 (autoAccept=null)
        toast.success('회원가입이 완료되었습니다!');
        navigate('/', { replace: true });
        return;
      } else {
        // 이메일 인증 필요
        toast.success('인증 메일을 발송했습니다.');
        const verifyParams = new URLSearchParams({ email: data.email });
        if (invitationToken) verifyParams.set('invitationToken', invitationToken);
        navigate(`/verify-email?${verifyParams.toString()}`);
      }
    } catch (err: unknown) {
      const errData = (err as { response?: { data?: { code?: string; error?: string; message?: string } } })?.response?.data;
      // CHG-141 EDGE-003: 토큰이 비-PENDING으로 변한 경우 명확한 안내
      if (errData?.code === 'INVITATION_NOT_PENDING') {
        toast.error(errData.message || '초대가 더 이상 유효하지 않습니다. 일반 가입으로 진행해주세요.');
        navigate('/register', { replace: true });
        return;
      }
      if (errData?.code === 'INVITATION_EMAIL_MISMATCH') {
        toast.error('초대 대상 이메일과 입력 이메일이 일치하지 않습니다.');
        return;
      }
      const msg = errData?.error ?? '회원가입에 실패했습니다.';
      toast.error(msg);
    }
  };

  // 초대 토큰이 비-PENDING이면 가입 폼 비활성화 + 안내 (EDGE-003)
  const inviteBlocked =
    !!invitationCtx && invitationCtx.status !== 'PENDING';
  const blockedLabel = invitationCtx && invitationCtx.status !== 'PENDING'
    ? INVITATION_STATUS_LABELS[invitationCtx.status]
    : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Wordmark className="text-3xl" />
          <p className="mt-1 text-sm text-gray-500">이슈 트래킹 시스템</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">회원가입</h2>

          {/* CHG-141 REQ-IF-012: 초대 컨텍스트 배너 */}
          {invitationCtx && !inviteBlocked && (
            <div className="mb-4 rounded-lg border border-primary-200 bg-primary-50 p-3 flex gap-3">
              <Mail className="size-5 text-primary-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-gray-700 font-medium">
                  {invitationCtx.type === 'ORG' ? '조직' : '프로젝트'} <strong>{invitationCtx.organizationName}</strong> 초대를 받으셨습니다.
                </p>
                <p className="text-gray-500 mt-0.5">
                  가입 후 이메일 인증을 완료하면 자동으로 합류됩니다.
                </p>
              </div>
            </div>
          )}

          {/* 초대가 비-PENDING (EDGE-003) — 폼 잠금 + 안내 */}
          {inviteBlocked && blockedLabel && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 flex gap-3">
              <AlertCircle className="size-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-gray-800 font-semibold mb-1">{blockedLabel.title}</p>
                <p className="text-gray-600 mb-2">{blockedLabel.message}</p>
                <Link to="/register" className="text-primary-600 font-medium hover:text-primary-700">
                  일반 가입으로 진행
                </Link>
              </div>
            </div>
          )}

          {invitationError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {invitationError}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">이름</label>
              <input
                id="name"
                type="text"
                autoComplete="name"
                placeholder="홍길동"
                className={`w-full px-3 py-2 border rounded-lg text-sm outline-none transition-colors
                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                  ${errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}`}
                {...register('name')}
              />
              {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="name@company.com"
                // CHG-141: 초대 토큰 정상 PENDING이면 readonly + 회색 배경
                readOnly={!!invitationCtx && !inviteBlocked}
                className={`w-full px-3 py-2 border rounded-lg text-sm outline-none transition-colors
                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                  ${errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}
                  ${invitationCtx && !inviteBlocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                {...register('email')}
              />
              {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">비밀번호</label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                placeholder="8자 이상, 영문+숫자"
                className={`w-full px-3 py-2 border rounded-lg text-sm outline-none transition-colors
                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                  ${errors.password ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}`}
                {...register('password')}
              />
              {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password.message}</p>}
            </div>

            <div>
              <label htmlFor="passwordConfirm" className="block text-sm font-medium text-gray-700 mb-1">비밀번호 확인</label>
              <input
                id="passwordConfirm"
                type="password"
                autoComplete="new-password"
                placeholder="비밀번호를 다시 입력하세요"
                className={`w-full px-3 py-2 border rounded-lg text-sm outline-none transition-colors
                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                  ${errors.passwordConfirm ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}`}
                {...register('passwordConfirm')}
              />
              {errors.passwordConfirm && <p className="mt-1 text-sm text-red-500">{errors.passwordConfirm.message}</p>}
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">역할</label>
              <select
                id="role"
                className={`w-full px-3 py-2 border rounded-lg text-sm outline-none transition-colors
                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                  ${errors.role ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}`}
                {...register('role')}
              >
                {ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {errors.role && <p className="mt-1 text-sm text-red-500">{errors.role.message}</p>}
            </div>

            <button
              type="submit"
              disabled={isSubmitting || invitationLoading || inviteBlocked}
              className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white py-2.5 px-4 rounded-lg text-sm font-medium
                hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <UserPlus className="size-4" />
              {isSubmitting ? '가입 중...' : invitationLoading ? '초대 정보 확인 중...' : '회원가입'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            이미 계정이 있으신가요?{' '}
            <Link to="/login" className="text-primary-600 font-medium hover:text-primary-700">
              로그인
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
